import { useCallback, useEffect, useState } from 'react';

import { t } from '@/utility/localization';

import {
  createCommunityFolder,
  deleteCommunityFolder,
  editCommunityFolder,
  getCommunityFolderItems,
  getCommunityFolderList,
  updateCommunityFolderOrder,
  updatePublishCommunityFolder
} from '@/services/community/communitiesLibraryService';

import {
  SORT_STATE_DEFAULT,
  SORT_STATE_OLDEST
} from '@/contexts/LibrarySetupContext/constants';

import {
  showSuccessToast,
  showToast
} from '@/components/common/ToastContainer';

const [CLASS, COLLECTION] = ['class', 'collection'];

const useFolderData = ({ communityId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [folderList, setFolderList] = useState([]);
  const [filteredFolderList, setFilteredFolderList] = useState([]);

  const [isFolderListLoading, setIsFolderListLoading] = useState(true);
  const [sortState, setSortState] = useState(SORT_STATE_DEFAULT);
  const [isFolderItemsLoading, setIsFolderItemsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  // filter folderList based on search term
  useEffect(() => {
    if (!searchTerm) return;

    setFilteredFolderList(
      folderList.filter(
        (folder) =>
          folder?.title
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          folder?.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  const getSortedData = (data) => {
    const sortedData = data?.sort((a, b) => {
      return new Date(a?.createdAt) - new Date(b?.createdAt);
    });
    return sortedData;
  };

  function selectSortState(selectedState) {
    setSortState(selectedState);
    let sortedData = [];
    if (selectedState === SORT_STATE_DEFAULT) {
      folderList.sort((a, b) => a.index - b.index);
      sortedData = folderList;
    } else {
      sortedData =
        sortState === SORT_STATE_OLDEST
          ? getSortedData(folderList)
          : getSortedData(folderList).reverse();
    }
    setFolderList(sortedData);
  }

  useEffect(() => {
    selectSortState(SORT_STATE_DEFAULT);
  }, []);

  const sortByIndex = (list) => list.sort((a, b) => a.index - b.index);

  const classFolderList =
    folderList
      ?.filter((folder) => folder?.type === CLASS)
      ?.sort((a, b) => a.index - b.index) ?? [];

  const collectionFolderList =
    folderList
      ?.filter((folder) => folder?.type === COLLECTION)
      ?.sort((a, b) => a.index - b.index) ?? [];

  const getFolderList = async ({ communityId }) => {
    try {
      if (!communityId) return;

      setIsFolderListLoading(true);

      const { data, error } = await getCommunityFolderList({
        communityId
      });

      if (error) throw new Error(error);

      setFolderList(sortByIndex(data));
      setFilteredFolderList(sortByIndex(data));
    } catch (e) {
      showToast({ text: e.message, type: 'error' });
    } finally {
      setIsFolderListLoading(false);
    }
  };

  const createFolder = async ({ params }) => {
    try {
      setIsLoading(true);

      const { data, error } = await createCommunityFolder({
        communityId,
        payload: params
      });

      if (error) throw new Error(error);

      const updatedFolderList = [...folderList, data];
      setFolderList(updatedFolderList);

      return { data };
    } catch (e) {
      return { error: e.message };
    } finally {
      setIsLoading(false);
    }
  };

  const editFolder = async ({ params, folderId }) => {
    try {
      setIsLoading(true);

      const { data, error } = await editCommunityFolder({
        communityId,
        folderId,
        payload: params
      });

      if (error) throw new Error(error);

      const updatedFolderIndex = folderList.findIndex(
        (folder) => folder._id === folderId
      );
      const tempFolderList = [...folderList];
      tempFolderList[updatedFolderIndex] = data;
      setFolderList(tempFolderList);

      return { data };
    } catch (e) {
      return { error: e };
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFolder = async ({ folderId }) => {
    try {
      setIsLoading(true);

      const { data, error } = await deleteCommunityFolder({
        communityId,
        folderId
      });

      if (error) throw new Error(error);

      const updatedFolderList = folderList.filter(
        (folder) => folder._id !== folderId
      );
      setFolderList(updatedFolderList);
      showSuccessToast(t('successfully-deleted'));

      return { data };
    } catch (e) {
      return { error: e.message };
    } finally {
      setIsLoading(false);
    }
  };

  const updatePublishFolder = useCallback(
    async ({ folderId, param }) => {
      try {
        setIsLoading(true);

        const { data, error } = await updatePublishCommunityFolder({
          communityId,
          folderId,
          payload: param
        });

        if (error) throw new Error(error);

        const updatedFolderIndex = folderList.findIndex(
          (folder) => folder._id === folderId
        );
        const tempFolderList = [...folderList];
        tempFolderList[updatedFolderIndex] = data;
        setFolderList(tempFolderList);

        return { data };
      } catch (e) {
        return { error: e.message };
      } finally {
        setIsLoading(false);
      }
    },
    [communityId, folderList]
  );

  const getFolderDetails = useCallback(
    async ({ folderId, communityId, async = false }) => {
      try {
        if (!async) setIsFolderItemsLoading(true);

        if (!communityId || !folderId) return;

        const { data, error } = await getCommunityFolderItems({
          communityId,
          folderId
        });

        if (error) throw new Error(error);

        return { data };
      } catch (e) {
        return { error: e.message };
      } finally {
        setIsFolderItemsLoading(false);
      }
    },
    []
  );

  const updateFolderOrder = async ({ communityId, payload }) => {
    try {
      setIsLoading(true);

      if (!communityId) return;

      const { data, error } = await updateCommunityFolderOrder({
        communityId,
        payload
      });

      if (error) throw new Error(error);

      return { data };
    } catch (e) {
      return { error: e.message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    folderList,
    isLoading,
    classFolderList,
    setSortState,
    selectSortState,
    sortState,
    setIsLoading,
    setSearchTerm,
    searchTerm,
    setFolderList,
    filteredFolderList,
    getFolderList,
    createFolder,
    editFolder,
    deleteFolder,
    updatePublishFolder,
    getFolderDetails,
    isFolderItemsLoading,
    isFolderListLoading,

    collectionFolderList,
    updateFolderOrder
  };
};

export default useFolderData;
